import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import {
  TrashIcon,
  PencilIcon,
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import ProConfirmation from "../../../utils/ProConfirmation";
import { TWclassNames } from "../../../utils/Div";
import { billingPlanItemsApi } from "../../../../api/billing_plan_items";
import { axiosWithToast } from "../../../Utils";
import { BillingPlanItemDialog } from "./billing-plan-item-dialog";
import { InvoiceBillingPlanItemDialog } from "./invoice-billing-plan-item-dialog";
import { useTranslation } from "react-i18next";

const MemoizedBillingPlanItemRow = React.memo(
  ({
    item,
    onEdit,
    onDelete,
    onInvoice,
    formatter,
    selected,
    onSelect,
    canEdit = true,
  }) => {
    const { t } = useTranslation();
    
    const getStatusIcon = (status) => {
      switch (status) {
        case "billed":
          return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
        case "planned":
          return <ClockIcon className="h-4 w-4 text-yellow-500" />;
        default:
          return null;
      }
    };

    const getStatusColor = (status) => {
      switch (status) {
        case "billed":
          return "bg-green-100 text-green-800";
        case "planned":
          return "bg-yellow-100 text-yellow-800";
        case "cancelled":
          return "bg-gray-100 text-gray-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("nb-NO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    };

    return (
      <TableRow hover key={item.id}>
        {canEdit ? (
        <TableCell padding="checkbox">
          {item.status === "planned" && (
            <input
              type="checkbox"
              checked={selected || false}
              onChange={(e) => onSelect && onSelect(item.id, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          )}
        </TableCell>
        ) : null}
        <TableCell align="left">
          <div className="font-medium text-gray-900">
            {item.description || t("project.billing_plan.no_description")}
          </div>
          {item.invoice_type === "a_account" && item.a_account_number && (
            <div className="text-sm text-blue-600 mt-1">
              {t("project.billing_plan.a_account")} {item.a_account_number}
            </div>
          )}
        </TableCell>
        <TableCell align="center">
          <span className={TWclassNames(
            "px-2 py-1 rounded-full text-xs font-medium",
            item.invoice_type === "a_account" 
              ? "bg-blue-100 text-blue-800" 
              : "bg-purple-100 text-purple-800"
          )}>
            {item.invoice_type === "a_account" 
              ? t("project.order_line.a_account") 
              : t("project.billing_plan.final_invoice")}
          </span>
        </TableCell>
        <TableCell align="center">
          <div className="text-gray-900">
            {formatDate(item.planned_date)}
          </div>
        </TableCell>
        <TableCell align="right">
          <div className="text-gray-900 font-medium">
            {formatter.format(item.amount || 0)}
          </div>
        </TableCell>
        <TableCell align="right">
          <div className="text-gray-900">
            {formatter.format(item.completed_amount || 0)}
          </div>
        </TableCell>
        <TableCell align="right">
          <div className="text-gray-900">
            {formatter.format(item.billed_amount || 0)}
          </div>
        </TableCell>
        <TableCell align="right">
          <div className="text-gray-900 font-medium">
            {formatter.format(item.remaining_amount || 0)}
          </div>
        </TableCell>
        <TableCell align="center">
          <span className={TWclassNames(
            "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
            getStatusColor(item.status)
          )}>
            {getStatusIcon(item.status)}
            <span className="ml-1">
              {item.status === "billed" 
                ? t("project.billing_plan.status.billed")
                : item.status === "planned"
                ? t("project.billing_plan.status.planned")
                : t("project.billing_plan.status.cancelled")}
            </span>
          </span>
          {item.customerinvoice && (
            <div className="text-xs text-gray-600 mt-1">
              {t("project.billing_plan.invoice_number")}: {item.customerinvoice.invoice_number}
            </div>
          )}
        </TableCell>
        <TableCell align="right">
          {canEdit ? (
          <div className="flex items-center justify-end space-x-2">
            {item.status === "planned" && onInvoice && (
              <button
                onClick={() => onInvoice(item)}
                className="text-green-600 hover:text-green-900"
                title={t("project.billing_plan.invoice")}
              >
                <CheckCircleIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => onEdit(item)}
              className="text-blue-600 hover:text-blue-900"
              title={t("project.billing_plan.edit")}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onDelete(item)}
              className="text-red-600 hover:text-red-900"
              title={t("project.billing_plan.delete")}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </TableCell>
      </TableRow>
    );
  }
);
MemoizedBillingPlanItemRow.displayName = "MemoizedBillingPlanItemRow";

export const BillingPlanItems = ({
  project,
  contractSum = 0,
  completedSum = 0,
  onRefresh,
  canEdit = true,
}) => {
  const { t } = useTranslation();
  const [billingPlanItems, setBillingPlanItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [invoicing, setInvoicing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [itemToInvoice, setItemToInvoice] = useState(null);

  const formatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
  });

  useEffect(() => {
    fetchBillingPlanItems();
  }, [project?.id]);

  useEffect(() => {
    if (typeof onRefresh !== "function") return;
    const billedSum = billingPlanItems
      .filter((item) => item.status === "billed")
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    onRefresh({ billedSum });
    // Inform parent totals when plan rows load; avoids stale billedAmount in overview cards.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to fetched rows
  }, [billingPlanItems]);

  const fetchBillingPlanItems = async () => {
    if (!project?.id) return;
    
    setLoading(true);
    try {
      const response = await billingPlanItemsApi.getBillingPlanItems(project.id);
      if (response.data?.success) {
        setBillingPlanItems(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching billing plan items:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setEditDialogOpen(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete || !project?.id) return;

    await axiosWithToast(
      billingPlanItemsApi.deleteBillingPlanItem(project.id, itemToDelete.id),
      () => {
        fetchBillingPlanItems();
      },
      () => {},
      () => {},
      t("project.billing_plan.deleting")
    );

    setDeleteConfirmationOpen(false);
    setItemToDelete(null);
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedItem(null);
  };

  const handleDialogSuccess = () => {
    fetchBillingPlanItems();
    handleDialogClose();
  };

  const handleSelectItem = (itemId, checked) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleInvoiceSelected = () => {
    if (selectedItems.size === 0) return;
    
    // Get first selected item
    const firstItemId = Array.from(selectedItems)[0];
    const item = billingPlanItems.find(i => i.id === firstItemId);
    
    if (item) {
      setItemToInvoice(item);
      setInvoiceDialogOpen(true);
    }
  };

  const handleInvoiceItem = (item) => {
    setItemToInvoice(item);
    setInvoiceDialogOpen(true);
  };

  const handleInvoiceDialogSuccess = () => {
    fetchBillingPlanItems();
    setInvoiceDialogOpen(false);
    setItemToInvoice(null);
    setSelectedItems(new Set());
  };

  const handleExport = async (format) => {
    setExporting(true);
    setExportFormat(format);
    try {
      const response = await billingPlanItemsApi.exportBillingPlanItems(project.id, format);
      // Create blob and download
      const blob = new Blob([response.data], {
        type: format === "excel" 
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `faktureringsplan_${project.id}_${new Date().toISOString().split('T')[0]}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting:", error);
    } finally {
      setExporting(false);
      setExportFormat(null);
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t("project.billing_plan.items.title")}
        </h3>
        <div className="flex space-x-2">
          {canEdit && selectedItems.size > 0 && (
            <button
              onClick={handleInvoiceSelected}
              disabled={invoicing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {invoicing ? t("project.billing_plan.invoicing") : t("project.billing_plan.invoice_selected")} ({selectedItems.size})
            </button>
          )}
          <div className="flex space-x-2">
            <button
              onClick={() => handleExport("excel")}
              disabled={exporting}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting && exportFormat === "excel" ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Laster...
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Excel
                </>
              )}
            </button>
            <button
              onClick={() => handleExport("pdf")}
              disabled={exporting}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting && exportFormat === "pdf" ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Laster...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  PDF
                </>
              )}
            </button>
          </div>
          {canEdit ? (
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {t("project.billing_plan.create_invoice")}
          </button>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <Table>
          <TableHead>
            <TableRow>
              {canEdit ? <TableCell padding="checkbox"></TableCell> : null}
              <TableCell>{t("project.order_line.description")}</TableCell>
              <TableCell align="center">{t("project.billing_plan.type")}</TableCell>
              <TableCell align="center">{t("project.billing_plan.planned_date")}</TableCell>
              <TableCell align="right">{t("project.billing_plan.amount")}</TableCell>
              <TableCell align="right">{t("project.billing_plan.completed_amount")}</TableCell>
              <TableCell align="right">{t("project.billing_plan.billed_amount")}</TableCell>
              <TableCell align="right">{t("project.billing_plan.remaining_amount")}</TableCell>
              <TableCell align="center">{t("project.billing_plan.status.title")}</TableCell>
              <TableCell align="right" style={{ width: 150 }}>{t("project.billing_plan.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {billingPlanItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 10 : 9} align="center" className="py-8 text-gray-500">
                  {t("project.billing_plan.items.empty")}
                </TableCell>
              </TableRow>
            ) : (
              billingPlanItems.map((item) => (
                <MemoizedBillingPlanItemRow
                  key={item.id}
                  item={item}
                  canEdit={canEdit}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onInvoice={handleInvoiceItem}
                  formatter={formatter}
                  selected={selectedItems.has(item.id)}
                  onSelect={handleSelectItem}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialogs */}
      <BillingPlanItemDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        project={project}
        billingPlanItem={selectedItem}
        contractSum={contractSum}
        contractLinesCompletedHint={completedSum}
        billedSum={billingPlanItems
          .filter(i => i.status === "billed")
          .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)}
      />

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("project.billing_plan.delete_confirmation")}
        action="delete"
        module="billing_plan_item"
        yesCallback={confirmDelete}
        cancelCallback={() => {
          setDeleteConfirmationOpen(false);
          setItemToDelete(null);
        }}
      />

      {itemToInvoice && (
        <InvoiceBillingPlanItemDialog
          open={invoiceDialogOpen}
          onClose={() => {
            setInvoiceDialogOpen(false);
            setItemToInvoice(null);
          }}
          onSuccess={handleInvoiceDialogSuccess}
          project={project}
          billingPlanItem={itemToInvoice}
          contractSum={contractSum}
          billedSum={billingPlanItems
            .filter((i) => i.status === "billed")
            .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)}
        />
      )}
    </div>
  );
};

